import { KeywordService } from "@/services/admin-services";
import { Keyword } from "@/services/admin-services/models/Keyword";
import { KeywordLabel } from "@/services/admin-services/models/KeywordLabel";
import { Language } from "@/services/global-services/models";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { FormEvent, Fragment, useState } from "react";

interface CreateDialogProps {
    open: boolean;
    element: string;
    keyword: Keyword;
    languages: Array<Language>;
    onClose: () => void;
    onSubmit: (keyword: Keyword) => void;
}

const CreateDialog = ({ open, element, keyword: activeKeyword, languages, onClose, onSubmit }: CreateDialogProps) => {
    const [keyword, setKeyword] = useState<Keyword>(activeKeyword);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const keywordService = new KeywordService("int");

    const onFormSubmit = (event: FormEvent) => {
        event.preventDefault();

        setIsSaving(true);

        let promise: Promise<Keyword>;

        if (keyword.id) {
            promise = keywordService.update(element, keyword.id, keyword);
        } else {
            promise = keywordService.create(element, keyword);
        }

        promise.then((keyword) => {
            setIsSaving(false);
            onSubmit(keyword);
        });
    };

    const getTranslation = (languageCode: string) => {
        const languageLabel = keyword.labels.find((x) => x.languageCode === languageCode);
        return languageLabel?.label || "";
    };

    const onChangeTranslation = (languageCode: string, value: string) => {
        const modifiedKeyword = { ...keyword };
        const languageLabel = modifiedKeyword.labels.find((x) => x.languageCode === languageCode);
        if (languageLabel) {
            languageLabel.label = value;
        } else {
            const newLanguageLabel: KeywordLabel = { languageCode: languageCode, label: value };
            modifiedKeyword.labels.push(newLanguageLabel);
        }

        setKeyword(modifiedKeyword);
    };

    return (
        <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth={"sm"} fullScreen={fullScreen}>
            <form onSubmit={(event) => onFormSubmit(event)}>
                {keyword.id ? (
                    <DialogTitle>Edit existing keyword</DialogTitle>
                ) : (
                    <DialogTitle>Add new keyword</DialogTitle>
                )}
                <DialogContent dividers>
                    {languages.map((language, index) => (
                        <Fragment key={index}>
                            <TextField
                                autoFocus
                                fullWidth
                                id={`Keyword - ${language.label}`}
                                value={getTranslation(language.slug)}
                                onChange={(event) => onChangeTranslation(language.slug, event.target.value)}
                                label={`Keyword - ${language.label}`}
                                type="text"
                                variant="standard"
                                sx={{ mb: 3 }}
                            />
                        </Fragment>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose()} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="contained" type="submit" disabled={isSaving}>
                        {keyword.id ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CreateDialog;
